/**
 * The legend half of the ★ map rules overlay (design v3, Variant 1), in the
 * action panel's place. Its rows and footer chips also draw the team play
 * rules, read-only, in the game menu's modal ({@link RulesReadOnlyList}).
 *
 * Phones: a sheet docked flush under the map bar, full width and always fully
 * expanded (maintainer, 2026-11-03): a "★ Special rules" header (the
 * overlay's title, read to screen readers as the whole rule list), every row
 * plus the board-wide chips and the "how dice work" key, scrolling when it is
 * taller than the space, and the "tap the map to return" hint pinned at its
 * foot. Tapping a row isolates that rule on the board. Screen readers close
 * the overlay with the escape gesture or the Close action (there is no
 * visible close button).
 * Desktop and landscape: a column over the action panel. Hovering a row
 * isolates it; clicking pins it.
 *
 * Dice chips (option B, "ATK d5 │ DEF d7") take taps of their own: a tap
 * expands the chip into a sentence with the odds under its row (and pins the
 * row). No pressable sits inside another: a row is a full-size pressable
 * layer with its content drawn over it, and the dice chips are siblings of
 * that layer (a <button> inside a <button> is invalid on web).
 */
import React from 'react'
import { Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import type { FooterChip, OverlayModel, OverlayRow, RowTile } from '../../../lib/ruleOverlayModel'
import type { OverlayPalette } from '../../../lib/rulesOverlayTheme'
import { TeamShape } from '../../game/TeamShape'
import { RuleGlyph } from './RuleGlyph'
import { RuleChip } from './RuleChip'

type IconName = React.ComponentProps<typeof MaterialCommunityIcons>['name']

/** Hidden from screen readers: the row's pressable layer speaks for its content. */
const decorative = Platform.OS === 'web'
  ? ({ 'aria-hidden': true } as const)
  : ({ accessible: false, importantForAccessibility: 'no-hide-descendants', accessibilityElementsHidden: true } as const)

function Tile({ tile, size }: { tile: RowTile; size: number }) {
  if (tile.kind === 'glyph') return <RuleGlyph glyph={tile.glyph} color={tile.color} size={size} />
  if (tile.kind === 'icon') return <MaterialCommunityIcons name={tile.name as IconName} size={size - 2} color={tile.color} />
  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <TeamShape team={tile.team} size={size} fill={tile.color} />
      <Text className="font-stencil" style={{ position: 'absolute', color: '#fff', fontSize: Math.round(size * 0.45) }}>{tile.initial}</Text>
    </View>
  )
}

/** X1: one line saying what normal dice are and what green and red mean. */
function DiceKey({ palette }: { palette: OverlayPalette }) {
  const swatch = (c: string) => <View style={{ width: 10, height: 10, borderRadius: 3, borderWidth: 1.5, borderColor: c, backgroundColor: `${c}33` }} />
  return (
    <View testID="rules-dice-key" style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', columnGap: 8, rowGap: 2, paddingTop: 6, paddingHorizontal: 4 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
        <MaterialCommunityIcons name="dice-6-outline" size={15} color={palette.ink} />
        <Text className="font-typed" style={{ fontSize: 12, color: palette.ink }}>Normal dice:</Text>
        <Text className="font-stencil" style={{ fontSize: 10, color: palette.ink }}>d6 v d6</Text>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
        {swatch(palette.good)}
        <Text className="font-typed" style={{ fontSize: 12, color: palette.ink }}>easier</Text>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
        {swatch(palette.bad)}
        <Text className="font-typed" style={{ fontSize: 12, color: palette.ink }}>harder to attack</Text>
      </View>
    </View>
  )
}

function FooterChips({ chips, palette, diceKey }: { chips: FooterChip[]; palette: OverlayPalette; diceKey: boolean }) {
  if (!chips.length && !diceKey) return null
  return (
    <View style={{ paddingTop: 2, paddingBottom: 2, marginTop: 4, borderTopWidth: 1, borderStyle: 'dashed', borderColor: palette.olive }}>
      {chips.length ? (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, paddingTop: 4, paddingHorizontal: 4 }}>
          {chips.map((c) => (
            <View key={c.label} style={{ flexDirection: 'row', alignItems: 'center', gap: 3, height: 24, borderRadius: 999, borderWidth: 1.5, borderColor: c.color, backgroundColor: palette.paper, paddingLeft: 4, paddingRight: 7 }}>
              {c.glyph ? <RuleGlyph glyph={c.glyph} color={c.color} size={15} /> : null}
              {c.icon ? <MaterialCommunityIcons name={c.icon as IconName} size={14} color={c.color} /> : null}
              {/* Ink text, the colour in the icon and border, so the chip reads on the dark sheet too. */}
              <Text className="font-typed" style={{ fontSize: 12, fontWeight: '700', color: palette.ink }}>{c.label}</Text>
            </View>
          ))}
        </View>
      ) : null}
      {diceKey ? <DiceKey palette={palette} /> : null}
    </View>
  )
}

function Row({
  row, palette, compact, pinned, expanded, onPress, onExpand, onHover, readOnly = false,
}: {
  row: OverlayRow; palette: OverlayPalette; compact: boolean; pinned: boolean
  /** Not hoverable or clickable (the team play rules modal). */
  readOnly?: boolean
  /** The index of the dice chip expanded under this row, if any. */
  expanded: number | null
  onPress: () => void
  onExpand: (chip: number) => void
  onHover?: (id: string | null) => void
}) {
  const [hovered, setHovered] = React.useState(false)
  const hover = (on: boolean) => {
    setHovered(on)
    onHover?.(on ? row.id : null)
  }
  // A pinned (clicked) rule shows every one of its explanations at once, so
  // nothing needs a second tap (maintainer, 2026-11-03); otherwise just the
  // chip that was tapped.
  const open = row.chips.map((chip, i) => ({ chip, i })).filter(({ chip, i }) => !!chip.expand && (pinned || expanded === i))
  const stacked = row.chips.length > 0 && row.chips.every((chip) => chip.line)
  return (
    <View
      testID={readOnly ? `rules-row-${row.id}` : undefined}
      // Read-only: the row itself speaks its rule (there is no pressable layer).
      accessible={readOnly || undefined}
      accessibilityLabel={readOnly ? row.accessibilityLabel : undefined}
      style={{ borderRadius: 12, padding: compact ? 4 : 6, backgroundColor: !readOnly && (pinned || hovered) ? palette.rowHighlight : 'transparent' }}>
      {/* The row's own pressable: a layer under its content, so the dice chips over it are siblings, not children. */}
      {readOnly ? null : <Pressable
        testID={`rules-row-${row.id}`}
        onPress={onPress}
        onHoverIn={onHover ? () => hover(true) : undefined}
        onHoverOut={onHover ? () => hover(false) : undefined}
        accessibilityRole="button"
        accessibilityLabel={row.accessibilityLabel}
        accessibilityHint={pinned ? 'Shows every rule again' : 'Shows only this rule on the map'}
        accessibilityState={{ selected: pinned }}
        aria-pressed={pinned}
        style={[StyleSheet.absoluteFill, { borderRadius: 12 }]}
      />}
      {/* Rows of plain lines (team play) stack: tile and label on top, the
          lines under them at full width, so they read in a narrow column. */}
      <View pointerEvents="box-none" style={stacked ? { gap: 6 } : { flexDirection: 'row', alignItems: 'center', gap: compact ? 8 : 10 }}>
        <View pointerEvents="none" style={{ flexDirection: 'row', alignItems: 'center', gap: compact ? 8 : 10 }}>
        <View pointerEvents="none" {...decorative} style={{ width: compact ? 30 : 34, height: compact ? 30 : 34, borderRadius: 9, borderWidth: 2, borderColor: row.color, backgroundColor: palette.paperUp, alignItems: 'center', justifyContent: 'center' }}>
          <Tile tile={row.tile} size={compact ? 20 : 24} />
        </View>
        <Text pointerEvents="none" {...decorative} className="font-stencil" style={{ ...(stacked ? { flex: 1 } : { width: compact ? 66 : 72 }), fontSize: compact ? 10 : 11, lineHeight: compact ? 12 : 13, color: palette.ink }}>{row.label}</Text>
        </View>
        <View pointerEvents="box-none" style={stacked ? { gap: 5, paddingLeft: compact ? 4 : 6 } : { flex: 1, flexDirection: 'row', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
          {row.chips.map((chip, i) => chip.expand ? (
            <Pressable
              key={i}
              testID={`rules-chip-${row.id}-${i}`}
              onPress={() => onExpand(i)}
              onHoverIn={onHover ? () => hover(true) : undefined}
              accessibilityRole="button"
              accessibilityLabel={chip.accessibilityLabel}
              accessibilityHint={pinned || expanded === i ? 'Hides the explanation' : 'Explains these dice'}
              accessibilityState={{ expanded: pinned || expanded === i }}
              aria-expanded={pinned || expanded === i}
              hitSlop={4}
              style={{ maxWidth: '100%', flexShrink: 1 }}
            >
              <RuleChip chip={chip} palette={palette} compact={compact} />
            </Pressable>
          ) : (
            <View key={i} pointerEvents="none" {...decorative} style={{ maxWidth: '100%', flexShrink: 1 }}>
              <RuleChip chip={chip} palette={palette} compact={compact} />
            </View>
          ))}
        </View>
      </View>
      {open.map(({ chip, i }, k) => (
        <View key={i} pointerEvents="none" testID={k === 0 ? `rules-chip-sentence-${row.id}` : `rules-chip-sentence-${row.id}-${i}`} style={{ marginTop: 6, borderRadius: 10, borderWidth: 1.5, borderColor: chip.color, backgroundColor: palette.paper, paddingHorizontal: 8, paddingVertical: 6 }}>
          <Text className="font-typed" style={{ fontSize: 13, lineHeight: 17, color: palette.ink }}>{chip.expand}</Text>
        </View>
      ))}
    </View>
  )
}

/**
 * Every row and the footer chips as a plain read-only list, outside the map
 * overlay: the team play rules modal (components/game/TeamRulesPanel).
 */
export function RulesReadOnlyList({ model, palette, compact = false }: { model: OverlayModel; palette: OverlayPalette; compact?: boolean }) {
  return (
    <View testID="rules-list">
      {model.rows.map((row) => (
        <Row key={row.id} row={row} palette={palette} compact={compact} pinned={false} expanded={null} onPress={() => {}} onExpand={() => {}} readOnly />
      ))}
      <FooterChips chips={model.footer} palette={palette} diceKey={!!model.diceKey} />
    </View>
  )
}

export interface RulesOverlayLegendProps {
  model: OverlayModel
  palette: OverlayPalette
  /** Phone sheet docked under the map bar (true) or desktop column (false). */
  sheet: boolean
  pinned: string | null
  onPin: (rowId: string | null) => void
  onHover: (rowId: string | null) => void
  /** Closes the overlay: the iOS escape gesture (two-finger scrub) while focus is in the legend. */
  onClose?: () => void
  maxHeight?: number
  /** Sheet: round the bottom corners (it ends above the bottom of the screen). */
  roundedBottom?: boolean
  onLayout?: (height: number) => void
}

export function RulesOverlayLegend({ model, palette, sheet, pinned, onPin, onHover, onClose, maxHeight, roundedBottom = true, onLayout }: RulesOverlayLegendProps) {
  // One expanded dice chip at a time: `${rowId}:${chipIndex}`.
  const [expanded, setExpanded] = React.useState<string | null>(null)
  const expandedIn = (rowId: string) => (expanded?.startsWith(`${rowId}:`) ? Number(expanded.slice(rowId.length + 1)) : null)
  const rowProps = (row: OverlayRow) => ({
    expanded: expandedIn(row.id),
    onExpand: (chip: number) => {
      // A chip on the pinned rule unpins it (its explanations close); on any
      // other rule it pins that rule, which opens all its explanations.
      if (pinned === row.id) { setExpanded(null); onPin(null); return }
      setExpanded(`${row.id}:${chip}`)
      onPin(row.id)
    },
  })
  const frame = {
    backgroundColor: palette.sheet, borderColor: palette.olive, borderWidth: 2, borderRadius: 18,
    ...(maxHeight ? { maxHeight } : null),
  }
  if (!sheet) {
    return (
      <View testID="rules-legend" onAccessibilityEscape={onClose} style={[frame, { paddingHorizontal: 12, paddingVertical: 10, gap: 2 }]} onLayout={(e) => onLayout?.(e.nativeEvent.layout.height)}>
        <Text className="font-stencil" style={{ fontSize: 10, color: palette.inkSoft, paddingHorizontal: 6, paddingTop: 2, paddingBottom: 4 }}>
          Hover a rule to isolate it on the map
        </Text>
        <ScrollView style={{ flexGrow: 0 }}>
          {model.rows.map((row) => (
            <Row key={row.id} row={row} palette={palette} compact={false} pinned={pinned === row.id} onPress={() => onPin(pinned === row.id ? null : row.id)} onHover={onHover} {...rowProps(row)} />
          ))}
          <FooterChips chips={model.footer} palette={palette} diceKey={!!model.diceKey} />
        </ScrollView>
      </View>
    )
  }
  const closeLabel = 'Close special rules'
  // Opaque, so the sheet reads as part of the bar it hangs from (nothing shows through).
  const fill = palette.sheet.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/, 'rgb($1,$2,$3)')
  return (
    <View
      testID="rules-legend"
      onAccessibilityEscape={onClose}
      onLayout={(e) => onLayout?.(e.nativeEvent.layout.height)}
      style={{
        backgroundColor: fill, borderColor: palette.olive, borderWidth: 2, borderTopWidth: 0,
        // Square where it meets the map bar; round at the foot only when it
        // ends above the bottom of the screen. The bottom border stays (in
        // the sheet's colour when it runs to the bottom) so the height never
        // changes with the corners.
        borderBottomLeftRadius: roundedBottom ? 18 : 0, borderBottomRightRadius: roundedBottom ? 18 : 0,
        ...(roundedBottom ? null : { borderBottomColor: fill }),
        ...(maxHeight ? { maxHeight } : null),
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 12, paddingTop: 8, paddingBottom: 4 }}>
        {/* Screen readers hear the whole rule list (the map's specialRules text) here. */}
        <View
          testID="rules-overlay-title"
          accessible
          accessibilityRole="header"
          accessibilityLabel={model.accessibilitySummary}
          accessibilityActions={onClose ? [{ name: 'escape', label: closeLabel }, { name: 'dismiss', label: closeLabel }] : undefined}
          onAccessibilityAction={(event) => { if (event.nativeEvent.actionName === 'escape' || event.nativeEvent.actionName === 'dismiss') onClose?.() }}
          style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}
        >
          <MaterialCommunityIcons name="star" size={16} color={palette.amber} />
          <Text className="font-stencil" style={{ fontSize: 15, lineHeight: 18, color: palette.ink }}>{model.title}</Text>
        </View>
        {model.rows.length > 1 ? (
          <Text testID="rules-legend-isolate-hint" {...decorative} className="font-stencil" style={{ flex: 1, textAlign: 'right', fontSize: 9, color: palette.inkSoft }}>
            Tap a rule to isolate it
          </Text>
        ) : null}
      </View>
      <ScrollView style={{ flexGrow: 0, flexShrink: 1 }} contentContainerStyle={{ paddingHorizontal: 8 }}>
        {model.rows.map((row) => (
          <Row key={row.id} row={row} palette={palette} compact pinned={pinned === row.id} onPress={() => onPin(pinned === row.id ? null : row.id)} {...rowProps(row)} />
        ))}
        <FooterChips chips={model.footer} palette={palette} diceKey={!!model.diceKey} />
      </ScrollView>
      <Text testID="rules-legend-hint" className="font-stencil" style={{ textAlign: 'center', fontSize: 10, letterSpacing: 0.4, color: palette.inkSoft, paddingTop: 4, paddingBottom: 8 }}>
        Tap the map to return
      </Text>
    </View>
  )
}
